import React from "react";

import { cursoCorto } from "../../utils/cursoCorto";
import { horarioBonito } from "../../utils/horarioBonito";
import { siguientePaso } from "../modals/payments/register/promoPasos";
import { GiftOption, GiftProduct, PromoOption, giftOptionsOf, initialGiftKey, round2 } from "./tipos";

/*
	Las tarjetas de una promo de adelanto: la opción (nombre, cuadra/faltan/sobra, qué cobra, qué regala, bloqueo con su siguiente paso)
	y las alternativas de regalo («Elige el Regalo»). Las comparten el panel de Caja (aplicarPromocion.tsx) y la Solicitud de Promoción
	de la asesora: una sola pinta, dos pantallas.
*/

export type Tone = "oro" | "acento" | "filo";
export const Pill: React.FC<{ t: string; tone: Tone }> = ({ t, tone }) => (
	<span style={{ fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 999, whiteSpace: "nowrap",
		background: tone === "oro" ? "var(--wa-oro-suave)" : tone === "acento" ? "var(--wa-acento-suave)" : "var(--wa-papel)",
		color: tone === "oro" ? "var(--wa-oro)" : tone === "acento" ? "var(--wa-acento-texto)" : "var(--wa-apagado)",
		border: tone === "filo" ? "1px solid var(--wa-filo)" : "none" }}>{t}</span>
);

export type Fit = { fits: "exacto" | "faltan" | "sobra" | null; diff: number };
/** Monto con símbolo: «$1,200.00» / «L 1,200.00». */
export const dinero = (sym: string, n: number) => (/^[A-Za-z]/.test(sym) ? sym + " " : sym) + round2(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fecha = (d: string | null) => d ? d.slice(8, 10) + "/" + d.slice(5, 7) + "/" + d.slice(0, 4) : "—";

export const FitPill: React.FC<{ o: PromoOption; fit: Fit; sym: string }> = ({ o, fit, sym }) =>
	fit.fits === "exacto" ? <Pill t={o.paidAlready ? "Ya Pagada" : "Cuadra"} tone="acento" />
		: fit.fits === "faltan" ? <Pill t={"Faltan " + dinero(sym, fit.diff)} tone="oro" />
			: fit.fits === "sobra" ? <Pill t={"Sobra " + dinero(sym, fit.diff)} tone="filo" /> : null;

export const productos = (items: GiftProduct[]) => items.map((i) => (Number(i.quantity) > 1 ? i.quantity + " × " : "") + i.name).join(" + ");
export const nombreRegalo = (g: GiftOption) => g.label || productos(g.items || []);
/** Lo que regala: meses / descuento / curso y, encima, los productos (la alternativa elegida, o «a elegir» si falta elegir). */
export const regala = (o: PromoOption, sym: string, key?: string | null): string => {
	const g = o.gives;
	const gs = giftOptionsOf(o);
	const partes: string[] = [];
	if (g) {
		if (g.discount !== undefined && g.discount !== null) partes.push("Descuento de " + dinero(sym, g.discount));
		else if (g.course) partes.push("Curso Regalado");
		else if (g.months && g.months.length) partes.push(g.months.join(", "));
	}
	const elegida = gs.find((x) => x.key === key && !x.blocked) || (gs.length === 1 && !gs[0].blocked ? gs[0] : null);
	if (elegida) partes.push(nombreRegalo(elegida));
	else if (gs.length === 1) { /* la única alternativa está bloqueada: no se dice que la regala (el motivo va aparte) */ }
	else if (gs.length > 1) partes.push("a elegir " + gs.map(nombreRegalo).join(" o "));
	else if (g?.products && g.products.length) partes.push(productos(g.products));
	else if (g?.kit) partes.push(g.kit.name);
	return partes.join(" + ");
};
/** Valor total de lo que regala: giftValue + la alternativa elegida (servidor viejo: el valor del kit). null = no se dice. */
export const valorRegalo = (o: PromoOption, key?: string | null): number | null => {
	const gs = giftOptionsOf(o);
	if (!gs.length) return o.gives?.kit && o.gives.kit.value > 0 ? o.gives.kit.value : null;
	const elegida = gs.find((x) => x.key === key && !x.blocked) || (gs.length === 1 && !gs[0].blocked ? gs[0] : null);
	if (!elegida) return null;
	return round2((Number(o.giftValue) || 0) + (Number(elegida.value) || 0));
};
export const cursoDe = (o: PromoOption) => [cursoCorto(o.courseName), o.scheduleText ? horarioBonito(o.scheduleText, { plural: true, separador: " " }) : ""].filter(Boolean).join(" ");

/** Regala, regalo bloqueado, meses que vuelven a precio normal, fecha límite, reemplaza. */
export const LineasExtra: React.FC<{ o: PromoOption; sym: string; giftKey?: string | null }> = ({ o, sym, giftKey }) => (
	<>
		{regala(o, sym, giftKey) ? <div>Regala: <b>{regala(o, sym, giftKey)}</b>{(valorRegalo(o, giftKey) || 0) > 0.009 ? " (" + dinero(sym, valorRegalo(o, giftKey) || 0) + ")" : ""}</div> : null}
		{giftOptionsOf(o).length === 1 && giftOptionsOf(o)[0].blocked && !o.blocked ? (
			<div style={{ color: "var(--wa-oro)", fontWeight: 600 }}>Regalo «{nombreRegalo(giftOptionsOf(o)[0])}»: {giftOptionsOf(o)[0].blocked}</div>
		) : null}
		{(o.reopenedMonths || []).length ? <div style={{ color: "var(--wa-oro)" }}>Vuelven a Precio Normal: <b>{(o.reopenedMonths || []).map((m) => m.title.trim() + " (+" + dinero(sym, m.difference) + ")").join(", ")}</b></div> : null}
		{o.deadline ? <div>Fecha Límite: <b>{fecha(o.deadline)}</b></div> : null}
		{o.replaces ? <div style={{ color: "var(--wa-oro)" }}>Reemplaza: <b>{o.replaces}</b>{o.reopens > 0.009 ? " (se suman " + dinero(sym, o.reopens) + ")" : ""}</div> : null}
	</>
);

/** Motivo + siguiente paso: dos renglones, nada más. */
export const Bloqueo: React.FC<{ o: PromoOption }> = ({ o }) => o.blocked ? (
	<div style={{ marginTop: 4 }}>
		<div style={{ color: "var(--wa-oro)", fontWeight: 600 }}>{o.blocked}</div>
		{siguientePaso(o.blocked) ? <div style={{ fontWeight: 700 }}>Siguiente Paso: <span style={{ fontWeight: 600 }}>{siguientePaso(o.blocked)}</span></div> : null}
	</div>
) : null;

const tarjeta = (on: boolean, blocked: boolean, clickable: boolean): React.CSSProperties => ({
	position: "static", display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 8px", margin: "6px 0", cursor: blocked || !clickable ? "default" : "pointer",
	border: "1px solid " + (on ? "var(--wa-acento)" : "var(--wa-filo)"), borderRadius: 6, background: on ? "var(--wa-acento-suave)" : "var(--wa-superficie)",
	whiteSpace: "normal", overflow: "visible", opacity: blocked ? 0.8 : 1, fontSize: 13,
});

interface TarjetaPromoProps {
	o: PromoOption; sym: string; fit: Fit;
	/** Lo que se cobra al escoger esta promo (charge sin los recargos que se anulan por pagar a tiempo) y el recargo real que lleva. */
	cargo: number; recargo?: number;
	/** Tarjeta de radio (Solicitud de Promoción): marcada o no. Sin `onPick` la tarjeta no se elige con un clic (Caja usa el botón `accion`). */
	selected?: boolean; onPick?: () => void; disabled?: boolean; radioName?: string;
	/** Botón a la derecha del nombre (Caja: «Elegir»). */
	accion?: React.ReactNode;
}
/** Una opción de promo como tarjeta: igual en Caja y en la Solicitud de Promoción. Las bloqueadas van grises con el motivo y el siguiente paso. */
export const TarjetaPromo: React.FC<TarjetaPromoProps> = ({ o, sym, fit, cargo, recargo, selected = false, onPick, disabled, radioName, accion }) => {
	const bloqueada = !!o.blocked;
	const cuerpo = (
		<span style={{ minWidth: 0, flex: 1 }}>
			<span style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
				<span style={{ minWidth: 0 }}><b>{o.name}</b> {o.agreed ? <Pill t="Acordada" tone="acento" /> : null} {bloqueada ? null : <FitPill o={o} fit={fit} sym={sym} />}</span>
				{bloqueada ? null : accion}
			</span>
			<div style={{ color: "var(--wa-apagado)" }}>{cursoDe(o)}</div>
			{bloqueada ? null : <div>Se Cobra: <b>{dinero(sym, cargo)}</b>{(recargo || 0) > 0.009 ? <span style={{ color: "var(--wa-apagado)" }}> (con {dinero(sym, recargo || 0)} de Recargo)</span> : null}</div>}
			{bloqueada ? null : <LineasExtra o={o} sym={sym} giftKey={initialGiftKey(o)} />}
			<Bloqueo o={o} />
		</span>
	);
	if (onPick) {
		return (
			<label style={tarjeta(selected, bloqueada, true)}>
				<input type="radio" name={radioName} style={{ marginTop: 3 }} checked={selected} disabled={disabled || bloqueada} onChange={() => onPick()} />
				{cuerpo}
			</label>
		);
	}
	return <div style={tarjeta(selected, bloqueada, false)}>{cuerpo}</div>;
};

interface EligeRegaloProps {
	o: PromoOption; sym: string; giftKey: string | null; disabled?: boolean; onChange: (key: string) => void;
}
/** «Elige el Regalo»: las alternativas de regalo como tarjetas de radio (solo cuando hay 2 o más). */
export const EligeRegalo: React.FC<EligeRegaloProps> = ({ o, sym, giftKey, disabled, onChange }) => {
	const gs = giftOptionsOf(o);
	if (gs.length < 2) return null;
	return (
		<div style={{ marginTop: 8 }}>
			<div style={{ fontWeight: 700 }}>Elige el Regalo</div>
			{gs.map((g) => {
				const on = giftKey === g.key;
				const total = round2((Number(o.giftValue) || 0) + (Number(g.value) || 0));
				return (
					<label key={"r" + g.key} style={{ ...tarjeta(on, !!g.blocked, true), margin: "3px 0", background: on ? "var(--wa-superficie)" : "transparent", opacity: g.blocked ? 0.75 : 1 }}>
						<input type="radio" name={"regalo-" + o.enrollmentId + "-" + o.promotionId} style={{ marginTop: 3 }} checked={on} disabled={disabled || !!g.blocked}
							onChange={() => onChange(g.key)} />
						<span style={{ minWidth: 0, flex: 1 }}>
							<span style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}><b>{nombreRegalo(g)}</b><span>{dinero(sym, Number(g.value) || 0)}</span></span>
							{g.label && (g.items || []).length ? <div style={{ color: "var(--wa-apagado)", fontSize: 12 }}>{productos(g.items)}</div> : null}
							{(Number(o.giftValue) || 0) > 0.009 ? <div style={{ color: "var(--wa-apagado)", fontSize: 12 }}>Regalo Total: {dinero(sym, total)}</div> : null}
							{g.blocked ? <div style={{ color: "var(--wa-oro)", fontWeight: 600, fontSize: 12 }}>{g.blocked}</div> : null}
						</span>
					</label>
				);
			})}
		</div>
	);
};
